import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import router from '@Site/Router'
import AuthInput from './AuthInput'
import SecurityChecklist from './SecurityChecklist'
import Button from '@SiteComponents/Button'
import LockIcon from '@Images/lock-icon.svg'
import BackLink from '@Images/back-link.svg'
import getParamValueFromUrl from '@Utils/GetParamValueFromUrl'

import {
  validatePassword,
  handleBlurField,
  handleChangeField,
  PASSWORD_MAX_LENGTH,
} from '@Utils/AuthenticationValidationHelpers'

import {
  getLoading,
} from '@SiteModules/Authentication/selectors'

import {
  updatePassword,
} from '@SiteModules/Authentication/actions'

import {
  FormErrors,
} from '@SiteModules/Authentication/types'

import './CreateNewPasswordForm.scss'

const MARGIN_TO_SHOW_CHAR_COUNT = 20

const ForgotPasswordForm: React.FC = () => {
  const dispatch = useDispatch()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const loading = useSelector(getLoading)
  const hasErrors = Object.values(errors).some(Boolean)
  const passwordsMatch = password === confirmPassword
  const isDisabled = !password.trim() || !confirmPassword.trim() || loading || !passwordsMatch || hasErrors

  const handlePasswordBlur = () => {
    handleBlurField('password', password, setTouched, setErrors, validatePassword)
  }

  const handleConfirmPasswordBlur = () => {
    handleBlurField('confirmPassword', confirmPassword, setTouched, setErrors, validatePassword)
  }

  const goToSignIn = () => {
    router.navigate('/users/sign_in')
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const resetPasswordToken = getParamValueFromUrl(window.location.href, 'reset_password_token')

    if (resetPasswordToken) {
      dispatch(updatePassword({ password, passwordConfirmation: confirmPassword, resetPasswordToken }))
    }
  }

  const handlePasswordChange = (val: string) => {
    setPassword(val)
    handleChangeField('password', val, touched, setErrors, validatePassword)
  }

  const handleConfirmPasswordChange = (val: string) => {
    setConfirmPassword(val)
    handleChangeField('confirmPassword', val, touched, setErrors, validatePassword)
  }

  const renderTitle = () => {
    return (
      <React.Fragment>
        {renderLockBadge()}
        <h1 className="FormPanel__title">Create new password</h1>
        <p className="FormPanel__subtitle">
          Your new password must be at least 8 characters long.
        </p>
      </React.Fragment>
    )
  }

  const renderLockBadge = () => {
    return (
      <div className="CreateNewPasswordForm__lock-badge">
        <div className="CreateNewPasswordForm__lock-badge-icon">
          <LockIcon />
        </div>
      </div>
    )
  }

  const renderInputs = () => {
    return (
      <React.Fragment>
        <input
          type="text"
          name="username"
          autoComplete="username"
          value=""
          readOnly
          hidden
          data-testid="reset-password-username-input"
        />
        <AuthInput
          id="reset-password-new-password"
          label="New Password"
          placeholder="Enter new password"
          value={password}
          autoComplete="new-password"
          data-testid="reset-password-new-password-input"
          onChange={handlePasswordChange}
          onBlur={handlePasswordBlur}
          error={errors.password}
          maxLength={PASSWORD_MAX_LENGTH}
          showCharCount={password.length > PASSWORD_MAX_LENGTH - MARGIN_TO_SHOW_CHAR_COUNT}
          isPassword
        />
        <SecurityChecklist password={password} />
        <AuthInput
          id="reset-password-confirmation"
          label="Confirm New Password"
          placeholder="Confirm your password"
          value={confirmPassword}
          autoComplete="new-password"
          data-testid="reset-password-confirmation-input"
          onChange={handleConfirmPasswordChange}
          onBlur={handleConfirmPasswordBlur}
          error={errors.confirmPassword}
          maxLength={PASSWORD_MAX_LENGTH}
          showCharCount={confirmPassword.length > PASSWORD_MAX_LENGTH - MARGIN_TO_SHOW_CHAR_COUNT}
          isPassword
        />
      </React.Fragment>
    )
  }

  const renderBottom = () => {
    return (
      <React.Fragment>
        <div className="FormPanel__button-area">
          <Button
            styleType="brand"
            size="large"
            caption="Reset Password"
            type="submit"
            disabled={isDisabled}
            data-testid="reset-password-submit"
          />
        </div>
        <button type="button" className="FormPanel__back-link" onClick={goToSignIn}>
          <BackLink />
          Back to log in
        </button>
      </React.Fragment>
    )
  }

  return (
    <form className="ForgotPasswordForm" onSubmit={handleSubmit} noValidate>
      {renderTitle()}
      {renderInputs()}
      {renderBottom()}
    </form>
  )
}

export default ForgotPasswordForm
